import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { authApi } from '@infrastructure/api';
const AuthContext = createContext(null);
export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    // Check for existing session on mount
    useEffect(() => {
        async function checkSession() {
            try {
                const session = await authApi.validateSession();
                if (session) {
                    setUser(session.user);
                }
            }
            catch {
                // Session invalid or expired, user remains null
            }
            finally {
                setIsLoading(false);
            }
        }
        checkSession();
    }, []);
    const login = useCallback(async (email, password) => {
        setIsLoading(true);
        setError(null);
        try {
            const session = await authApi.login({ email, password });
            setUser(session.user);
        }
        catch (err) {
            const message = err instanceof Error ? err.message : 'Login failed';
            setError(message);
            throw err;
        }
        finally {
            setIsLoading(false);
        }
    }, []);
    const logout = useCallback(async () => {
        setIsLoading(true);
        try {
            await authApi.logout();
            setUser(null);
        }
        catch {
            // Even if logout fails on server, clear local state
            setUser(null);
        }
        finally {
            setIsLoading(false);
        }
    }, []);
    const clearError = useCallback(() => {
        setError(null);
    }, []);
    const value = useMemo(() => ({
        user,
        isAuthenticated: user !== null,
        isLoading,
        login,
        logout,
        error,
        clearError,
    }), [user, isLoading, login, logout, error, clearError]);
    return _jsx(AuthContext.Provider, { value: value, children: children });
}
export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
